/**
 * MIN-298 — createAgentChat: one face, two ways in.
 *
 * `AgentChat.Provider` holds ONE client instance (built by the host via
 * `createAgentChat`, e.g. `createAgentChat({ auth: medplum, baseUrl:
 * AI_ENGINE_URL, defaults: { role: "clinical" } })`); hooks just read
 * it off context. The headless face and this React layer share the exact
 * same implementation, so they can't drift.
 *
 * Every hook here throws if rendered with no `AgentChat.Provider` above it —
 * deliberately no fallback to a default URL or an unauthenticated request.
 *
 * Navigation stays app-side, per MIN-290: `useCreateThread` resolves with the
 * created thread and routes nowhere.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext } from "react";
import type { AgentChat as AgentChatClient, CreateThreadInput } from "./create-agent-chat";
import { chatThreadQueries } from "./queries";
import type { ChatThread, ThreadId } from "./thread";

const AgentChatContext = createContext<AgentChatClient | undefined>(undefined);

export interface AgentChatProviderProps {
  /** A client built by `createAgentChat` — the provider holds this exact
   *  instance, never rebuilding it, so every surface under it shares one. */
  client: AgentChatClient;
  children: ReactNode;
}

function Provider({ client, children }: AgentChatProviderProps) {
  return <AgentChatContext value={client}>{children}</AgentChatContext>;
}

/** The `AgentChat` namespace: `<AgentChat.Provider client={...}>`. */
export const AgentChat = { Provider };

/** The client an `AgentChat.Provider` above supplies. Throws with no provider above. */
export function useAgentChat(): AgentChatClient {
  const client = useContext(AgentChatContext);
  if (!client) {
    throw new Error("useAgentChat must be called inside an AgentChat.Provider");
  }
  return client;
}

/**
 * Creates a thread through the provider's client — defaults merge under the
 * caller's own fields there, exactly as MIN-290 established. Resolves with
 * the created thread; routing (or not) is left to the caller's `onSuccess`.
 */
export function useCreateThread() {
  const client = useAgentChat();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input?: CreateThreadInput): Promise<ChatThread> => client.createThread(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatThreadQueries.all() });
    },
  });
}

/**
 * Deletes a thread through the provider's client — the client itself evicts
 * the deleted thread's conversation from the registry; this just invalidates
 * the thread list so surfaces stop showing it.
 */
export function useDeleteThread() {
  const client = useAgentChat();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: ThreadId): Promise<void> => client.deleteThread(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatThreadQueries.all() });
    },
  });
}
